import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}



/* rodin-wa-mini-text-fix-2026-05-21: replace stale cached PDP WhatsApp mini button copy. */
(function(){
  function fixRodinWaMini(){
    document.querySelectorAll('a.rodin-wa-mini-help__link').forEach(function(a){
      if (a.textContent && a.textContent.indexOf('Kurze Frage?') !== -1) {
        var span = a.querySelector('span:last-child') || a;
        span.textContent = 'Unsicher? WhatsApp fragen';
        a.href = 'https://wa.me/491621004061?text=Hallo%20Rodin%2C%20ich%20bin%20unsicher%20ob%20das%20Lenkrad%20zu%20meinem%20Fahrzeug%20passt.';
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fixRodinWaMini);
  else fixRodinWaMini();
  setTimeout(fixRodinWaMini, 600);
})();

/* RODIN_UPSELL_LABEL_ALIGN_ASSET_FIX_V1: native upsell copy + hard-right price fallback for cached PDP HTML. */
(function(){
  if (window.__rodinUpsellLabelAlignAssetFixV1) return;
  window.__rodinUpsellLabelAlignAssetFixV1 = true;
  function ensureStyle(){
    if (document.getElementById('rodin-upsell-label-align-asset-fix-v1')) return;
    var style = document.createElement('style');
    style.id = 'rodin-upsell-label-align-asset-fix-v1';
    style.textContent = '.rodin-native-upsell__item{grid-template-columns:22px 46px minmax(0,1fr)!important;padding-right:96px!important;position:relative!important}.rodin-native-upsell__item>span:not(.rodin-native-upsell__price){min-width:0!important}.rodin-native-upsell__price{position:absolute!important;right:10px!important;top:50%!important;transform:translateY(-50%)!important;justify-self:end!important;text-align:right!important;min-width:78px!important;margin-left:auto!important;white-space:nowrap!important}@media(max-width:749px){.rodin-native-upsell__item{grid-template-columns:22px 42px minmax(0,1fr)!important;padding-right:84px!important}.rodin-native-upsell__price{right:8px!important;min-width:68px!important}}';
    document.head.appendChild(style);
  }
  function apply(){
    ensureStyle();
    document.querySelectorAll('.rodin-native-upsell__title').forEach(function(el){
      if (/Wird oft zusammen gekauft/i.test(el.textContent || '')) el.textContent = 'Wähle deine Zusatzausstattung';
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, { once: true });
  else apply();
  document.addEventListener('shopify:section:load', apply);
})();

/* RODIN_AUDI_NATIVE_UPSELL_FIX_V1: Audi native upsell override after PAO removal/cache lag.
   Audi: no ACC, Vibration €59 (variant 58080003129689), Lenkradheizung €149 (variant 58080009421145). BMW untouched. */
(function(){
  if (window.__rodinAudiNativeUpsellFixV1) return;
  window.__rodinAudiNativeUpsellFixV1 = true;
  function isAudiPdp(){
    var path = String(window.location && window.location.pathname || '').toLowerCase();
    var title = String(document.title || '').toLowerCase();
    return path.indexOf('/products/') !== -1 && (path.indexOf('audi') !== -1 || title.indexOf('audi') !== -1);
  }
  function priceNode(label){
    return label && label.querySelector ? label.querySelector('.rodin-native-upsell__price') : null;
  }
  function setItem(name, variantId, priceText){
    document.querySelectorAll('.rodin-native-upsell__item').forEach(function(label){
      var cb = label.querySelector('.rodin-native-upsell__check');
      var itemName = label.querySelector('.rodin-native-upsell__name');
      var title = (cb && cb.getAttribute('data-rodin-upsell-title')) || (itemName && itemName.textContent) || '';
      if (String(title).trim().toLowerCase() === name.toLowerCase()) {
        if (cb) cb.setAttribute('data-rodin-upsell-variant-id', String(variantId));
        var p = priceNode(label); if (p) p.textContent = priceText;
      }
    });
  }
  function apply(){
    if (!isAudiPdp()) return;
    document.querySelectorAll('.rodin-native-upsell__item').forEach(function(label){
      var cb = label.querySelector('.rodin-native-upsell__check');
      var itemName = label.querySelector('.rodin-native-upsell__name');
      var title = (cb && cb.getAttribute('data-rodin-upsell-title')) || (itemName && itemName.textContent) || '';
      if (String(title).trim().toLowerCase() === 'acc') label.remove();
    });
    setItem('Vibration', '58080003129689', '€59,00');
    setItem('Lenkradheizung', '58080009421145', '€149,00');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, { once: true }); else apply();
  setTimeout(apply, 350);
  setTimeout(apply, 1200);
  document.addEventListener('shopify:section:load', apply);
})();

/* RODIN_NATIVE_UPSELL_RENDER_FALLBACK_V2: render native Zusatzausstattung when Shopify/app cache omits the custom-liquid block; adds Mercedes Distronic. */
(function(){
  if (window.__rodinNativeUpsellRenderFallbackV2) return;
  window.__rodinNativeUpsellRenderFallbackV2 = true;
  function pageText(){ return (String(location.pathname || '') + ' ' + String(document.title || '')).toLowerCase(); }
  function brand(){
    var t = pageText();
    if (t.indexOf('/products/') === -1) return '';
    if (t.indexOf('audi') !== -1) return 'audi';
    if (t.indexOf('vw') !== -1 || t.indexOf('volkswagen') !== -1) return 'vw';
    if (t.indexOf('porsche') !== -1) return 'porsche';
    if (t.indexOf('bmw') !== -1) return 'bmw';
    if (t.indexOf('mercedes') !== -1 || t.indexOf('mercedez') !== -1 || t.indexOf('benz') !== -1) return 'mercedes';
    return '';
  }
  function addStyle(){
    if (document.getElementById('rodin-native-upsell-fallback-v2-style')) return;
    var style=document.createElement('style');
    style.id='rodin-native-upsell-fallback-v2-style';
    style.textContent='.rodin-native-upsell{margin:0 0 12px;padding:14px;border:1px solid rgba(18,8,12,.14);border-radius:14px;background:#fff;box-shadow:0 8px 22px rgba(18,8,12,.045);color:#12080c}.rodin-native-upsell__title{font-size:.92rem;font-weight:800;margin:0 0 10px;letter-spacing:-.01em;color:#12080c}.rodin-native-upsell__grid{display:grid;gap:8px}.rodin-native-upsell__item{display:grid;grid-template-columns:22px 46px minmax(0,1fr);align-items:center;gap:10px;padding:8px 96px 8px 8px;position:relative;border:1px solid rgba(18,8,12,.10);border-radius:12px;background:#fff;cursor:pointer;transition:border-color .15s ease,box-shadow .15s ease,transform .15s ease}.rodin-native-upsell__item:hover{border-color:rgba(18,8,12,.24);box-shadow:0 6px 16px rgba(18,8,12,.06);transform:translateY(-1px)}.rodin-native-upsell__check{width:18px;height:18px;accent-color:#12080c;cursor:pointer}.rodin-native-upsell__img{width:46px;height:46px;object-fit:contain;border-radius:8px;background:#f7f1e6;padding:4px}.rodin-native-upsell__name{font-size:.9rem;font-weight:750;line-height:1.15;color:#12080c;white-space:normal;overflow:hidden;text-overflow:ellipsis;display:block}.rodin-native-upsell__sub{display:block;margin-top:2px;font-size:.73rem;font-weight:500;color:rgba(18,8,12,.68);line-height:1.18}.rodin-native-upsell__price{position:absolute;right:10px;top:50%;transform:translateY(-50%);font-size:.88rem;font-weight:800;color:#12080c;white-space:nowrap;text-align:right;min-width:78px}.rodin-native-upsell__note{margin:9px 0 0;font-size:.76rem;line-height:1.28;color:rgba(18,8,12,.72)}@media(max-width:749px){.rodin-native-upsell{padding:12px}.rodin-native-upsell__item{grid-template-columns:22px 42px minmax(0,1fr);gap:8px;padding:8px 84px 8px 8px}.rodin-native-upsell__img{width:42px;height:42px}.rodin-native-upsell__name{font-size:.86rem}.rodin-native-upsell__price{right:8px;font-size:.84rem;min-width:68px}}.rodin-under-atc-review-clone{margin:8px 0 12px;padding:10px;border:1px solid rgba(18,8,12,.10);border-radius:14px;background:#fff;box-shadow:0 8px 22px rgba(18,8,12,.045);overflow:hidden}.rodin-under-atc-review-clone *{max-width:100%}.rodin-under-atc-review-clone [class*=swiper],.rodin-under-atc-review-clone [class*=carousel]{max-width:100%}';
    document.head.appendChild(style);
  }
  function cfg(b){
    if (b === 'audi') return [
      ['Vibration','Spurhalte-/Vibrationsfunktion','58080003129689','€59,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/icons8-vibration-100.png?v=1768484901'],
      ['Lenkradheizung','Komfort-Option vor Fertigung','58080009421145','€149,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/lenkradheizung-8581848.png?v=1769665210']
    ];
    if (b === 'vw') return [
      ['ACC','nur bei passender Fahrzeugausstattung','58270547116377','€49,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/acc-8834440.png?v=1769665217'],
      ['Vibration','Spurhalte-/Vibrationsfunktion','58080003129689','€59,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/icons8-vibration-100.png?v=1768484901'],
      ['Lenkradheizung','Komfort-Option vor Fertigung','58080009421145','€149,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/lenkradheizung-8581848.png?v=1769665210']
    ];
    if (b === 'porsche') return [
      ['Vibration','Spurhalte-/Vibrationsfunktion','58270548132185','€99,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/icons8-vibration-100.png?v=1768484901'],
      ['Lenkradheizung','Komfort-Option vor Fertigung','57075014697305','€199,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/lenkradheizung-8581848.png?v=1769665210']
    ];
    if (b === 'mercedes') return [
      ['Distronic','nur bei passender Fahrzeugausstattung','57074994053465','€59,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/acc-8834440.png?v=1769665217'],
      ['Vibration','Spurhalte-/Vibrationsfunktion','57074998083929','€149,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/icons8-vibration-100.png?v=1768484901'],
      ['Lenkradheizung','Komfort-Option vor Fertigung','57075014697305','€199,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/lenkradheizung-8581848.png?v=1769665210']
    ];
    if (b === 'bmw') return [
      ['ACC','nur bei passender Fahrzeugausstattung','57074994053465','€59,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/acc-8834440.png?v=1769665217'],
      ['Vibration','Spurhalte-/Vibrationsfunktion','57074998083929','€149,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/icons8-vibration-100.png?v=1768484901'],
      ['Lenkradheizung','Komfort-Option vor Fertigung','57075014697305','€199,00','//cdn.shopify.com/s/files/1/1004/0714/8889/files/lenkradheizung-8581848.png?v=1769665210']
    ];
    return [];
  }
  function itemHtml(x){
    return '<label class="rodin-native-upsell__item"><input class="rodin-native-upsell__check" type="checkbox" data-rodin-upsell-variant-id="'+x[2]+'" data-rodin-upsell-title="'+x[0]+'"><img class="rodin-native-upsell__img" src="'+x[4]+'" alt="'+x[0]+'" loading="lazy"><span><span class="rodin-native-upsell__name">'+x[0]+'</span><span class="rodin-native-upsell__sub">'+x[1]+'</span></span><span class="rodin-native-upsell__price">'+x[3]+'</span></label>';
  }
  function productForm(btn){return (btn && btn.closest && btn.closest('form[action*="/cart/add"]')) || document.querySelector('product-form-component form[action*="/cart/add"], form[action*="/cart/add"]');}
  function insertUpsell(){
    var b=brand(), form=productForm();
    if (!b || !form || document.querySelector('.rodin-native-upsell')) return;
    addStyle();
    var rows=cfg(b); if (!rows.length) return;
    var box=document.createElement('div');
    box.className='rodin-native-upsell';
    box.setAttribute('data-rodin-native-upsell','true');
    box.innerHTML='<p class="rodin-native-upsell__title">Wähle deine Zusatzausstattung</p><div class="rodin-native-upsell__grid">'+rows.map(itemHtml).join('')+'</div><p class="rodin-native-upsell__note">Wir prüfen vor Fertigung, ob die gewählten Funktionen zu deinem Fahrzeug passen.</p>';
    form.parentNode.insertBefore(box, form);
  }
  function selectedUpsells(){return Array.from(document.querySelectorAll('.rodin-native-upsell__check:checked')).map(function(cb){return Number(cb.getAttribute('data-rodin-upsell-variant-id'));}).filter(Boolean);}
  function baseVariant(form){var input=form && form.querySelector('input[name="id"]'); return input ? Number(input.value) : 0;}
  function qty(form){var input=form && form.querySelector('input[name="quantity"], quantity-selector-component input'); var n=input ? Number(input.value) : 1; return isFinite(n) && n>0 ? n : 1;}
  async function addItems(form, btn){
    var id=baseVariant(form), upsells=selectedUpsells(); if(!id || !upsells.length) return;
    if(btn && btn.dataset.rodinNativeBusy==='1') return;
    if(btn){btn.dataset.rodinNativeBusy='1'; btn.setAttribute('aria-busy','true');}
    var items=[{id:id, quantity:qty(form)}].concat(upsells.map(function(v){return {id:v, quantity:1};}));
    try{
      var r=await fetch('/cart/add.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:items})});
      if(!r.ok) throw new Error('cart add failed '+r.status);
      window.location.href='/cart';
    }catch(e){
      if(form){ try{ HTMLFormElement.prototype.submit.call(form); }catch(_){ form.submit(); } }
    }
  }
  if (!window.__rodinNativeUpsellAtcFallbackV2) {
    window.__rodinNativeUpsellAtcFallbackV2 = true;
    document.addEventListener('click', function(ev){
      var btn=ev.target && ev.target.closest ? ev.target.closest('button[name="add"], button[data-testid="standalone-add-to-cart"], button.add-to-cart-button, .sticky-add-to-cart__button') : null;
      if(!btn || !selectedUpsells().length) return;
      var form=productForm(btn); if(!form) return;
      ev.preventDefault(); ev.stopPropagation(); if(ev.stopImmediatePropagation) ev.stopImmediatePropagation();
      addItems(form, btn);
    }, true);
  }
  function underAtcContext(){
    var details=document.querySelector('[data-testid="product-information-details"], .product-details');
    var form=productForm();
    var anchor=form && (form.closest('product-form-component') || form);
    var parent=anchor && anchor.parentNode;
    return { details: details, form: form, anchor: anchor, parent: parent };
  }
  function directChildWith(parent, test){
    if(!parent) return null;
    return Array.from(parent.children || []).find(test) || null;
  }
  function isReviewBlock(el){
    var txt=el && (el.innerText || el.textContent || '');
    return !!(txt.indexOf('Unsere Kunden lieben uns') !== -1 && txt.indexOf('Bewertungen') !== -1);
  }

  function removeEmptyUnderAtcReviewClones(){
    try{
      Array.from(document.querySelectorAll('.rodin-under-atc-review-clone')).forEach(function(el){
        var txt=(el.innerText||'').trim();
        if(!txt || txt.indexOf('Unsere Kunden lieben uns') === -1){ el.remove(); }
      });
    }catch(e){}
  }
  function enforceBadgesBeforeReviews(){
    var ctx=underAtcContext();
    if(!ctx.details || !ctx.anchor || !ctx.parent) return;
    var badge=directChildWith(ctx.parent, function(el){ return el.querySelector && el.querySelector('.rodin-static-trust-badges'); });
    var review=directChildWith(ctx.parent, function(el){ return el !== badge && (el.classList && el.classList.contains('rodin-under-atc-review-clone') || isReviewBlock(el)); });
    if(badge){
      badge.style.setProperty('order','30','important');
      badge.style.setProperty('display','block','important');
      badge.style.setProperty('min-height','0','important');
    }
    if(review){
      review.style.setProperty('order','40','important');
      review.style.setProperty('margin-top','12px','important');
    }
    if(badge && ctx.anchor.nextSibling !== badge) ctx.parent.insertBefore(badge, ctx.anchor.nextSibling);
    if(badge && review && badge.nextSibling !== review) ctx.parent.insertBefore(review, badge.nextSibling);
  }
  function cloneReviewsUnderAtc(){
    var b=brand(); if (!b) return;
    var ctx=underAtcContext();
    if(!ctx.details || !ctx.anchor || !ctx.parent) return;
    var existing=directChildWith(ctx.parent, function(el){ return isReviewBlock(el); });
    if(!existing){
      var candidates=Array.from(document.querySelectorAll('section, .shopify-section, div')).filter(function(el){
        return isReviewBlock(el) && !ctx.details.contains(el);
      });
      var src=candidates.sort(function(a,b){ return (a.innerText || '').length - (b.innerText || '').length; })[0];
      if(src){
        addStyle();
        var clone=src.cloneNode(true);
        clone.classList.add('rodin-under-atc-review-clone');
        clone.removeAttribute('id');
        var badge=directChildWith(ctx.parent, function(el){ return el.querySelector && el.querySelector('.rodin-static-trust-badges'); });
        ctx.parent.insertBefore(clone, badge ? badge.nextSibling : ctx.anchor.nextSibling);
      }
    }
    enforceBadgesBeforeReviews();
  }
  function apply(){ insertUpsell(); cloneReviewsUnderAtc(); removeEmptyUnderAtcReviewClones(); enforceBadgesBeforeReviews(); removeEmptyUnderAtcReviewClones(); }
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, {once:true}); else apply();
  [50,150,300,600,1000,1600,2400,3600,5200,8000].forEach(function(ms){ setTimeout(apply, ms); });
  try{
    var mo=new MutationObserver(function(){ apply(); });
    mo.observe(document.documentElement,{childList:true,subtree:true});
    setTimeout(function(){ try{mo.disconnect();}catch(e){} },15000);
  }catch(e){}
  document.addEventListener('shopify:section:load', apply);
})();

